import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceAlerts = ({ className = '' }) => {
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Mock compliance alerts data
  const mockAlerts = [
    {
      id: 'alert-001',
      type: 'warning',
      severity: 'medium',
      title: 'Potential Re-wrapping Detected',
      description: 'Credit VCS-2024-001-12345 shows signs of potential re-wrapping across multiple tokenization platforms.',
      timestamp: '2024-09-20T14:30:00Z',
      affectedCredits: ['VCS-2024-001-12345'],
      recommendation: 'Verify tokenization history and ensure single-platform tokenization compliance.',
      status: 'active',
      details: {
        riskLevel: 'Medium',
        platforms: ['Toucan Protocol', 'Moss.Earth'],
        lastActivity: '2024-09-20T12:15:00Z'
      }
    },
    {
      id: 'alert-002',
      type: 'error',
      severity: 'high',
      title: 'Double Counting Risk',
      description: 'Credits retired by Global Carbon Corp may conflict with host country NDC accounting.',
      timestamp: '2024-09-19T09:45:00Z',
      affectedCredits: ['VCS-2024-002-67890', 'VCS-2024-002-67891'],
      recommendation: 'Verify Article 6 corresponding adjustment status before proceeding with retirement claims.',
      status: 'active',
      details: {
        riskLevel: 'High',
        hostCountry: 'Brazil',
        ndcConflict: true,
        article6Status: 'Pending CA'
      }
    },
    {
      id: 'alert-003',
      type: 'info',
      severity: 'low',
      title: 'Methodology Transition Available',
      description: 'VM0007 methodology has been updated. Credits under old version may benefit from transition.',
      timestamp: '2024-09-18T16:20:00Z',
      affectedCredits: ['VCS-2024-001-12345', 'VCS-2024-003-11111'],
      recommendation: 'Consider methodology transition to maintain highest integrity standards.',
      status: 'resolved',
      details: {
        riskLevel: 'Low',
        oldMethodology: 'VM0007 v1.6',
        newMethodology: 'VM0007 v2.0',
        transitionDeadline: '2024-12-31'
      }
    },
    {
      id: 'alert-004',
      type: 'warning',
      severity: 'medium',
      title: 'Serial Number Reuse Detected',
      description: 'Serial number pattern suggests potential duplicate issuance across registries.',
      timestamp: '2024-09-17T11:10:00Z',
      affectedCredits: ['GS-2024-001-54321'],
      recommendation: 'Cross-reference with other registries to prevent duplicate counting.',
      status: 'investigating',
      details: {
        riskLevel: 'Medium',
        suspiciousPattern: 'Similar serial structure',
        registriesInvolved: ['Gold Standard', 'ACR'],
        investigationStatus: 'In Progress'
      }
    }
  ];

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'error': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getAlertBgColor = (type) => {
    switch (type) {
      case 'error': return 'bg-error/10 border-error/20';
      case 'warning': return 'bg-warning/10 border-warning/20';
      case 'info': return 'bg-primary/10 border-primary/20';
      default: return 'bg-muted/10 border-border';
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-success text-success-foreground'
    };
    return colors?.[severity] || 'bg-muted text-muted-foreground';
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-error/10 text-error',
      investigating: 'bg-warning/10 text-warning',
      resolved: 'bg-success/10 text-success'
    };
    return colors?.[status] || 'bg-muted/10 text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const activeAlerts = mockAlerts?.filter(alert => alert?.status === 'active');
  const resolvedAlerts = mockAlerts?.filter(alert => alert?.status !== 'active');

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Shield" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Compliance Alerts</h3>
          {activeAlerts?.length > 0 && (
            <span className="px-2 py-1 bg-error text-error-foreground text-xs font-medium rounded-full">
              {activeAlerts?.length} active
            </span>
          )}
        </div>
        <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">
          View All
        </button>
      </div>
      {/* Active Alerts */}
      {activeAlerts?.length > 0 && (
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Active Alerts</h4>
          {activeAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-smooth ${getAlertBgColor(alert?.type)}`}
              onClick={() => setSelectedAlert(selectedAlert === alert?.id ? null : alert?.id)}
            >
              <div className="flex items-start space-x-3">
                <Icon 
                  name={getAlertIcon(alert?.type)} 
                  size={20} 
                  className={`mt-0.5 ${getAlertColor(alert?.type)}`} 
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-foreground">{alert?.title}</h5>
                      <p className="text-xs text-muted-foreground mt-1">{alert?.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityBadge(alert?.severity)}`}>
                        {alert?.severity?.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(alert?.status)}`}>
                        {alert?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>{formatTimestamp(alert?.timestamp)}</span>
                      <span>{alert?.affectedCredits?.length} credits affected</span>
                    </div>
                    <Icon 
                      name={selectedAlert === alert?.id ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                      className="text-muted-foreground" 
                    />
                  </div>

                  {/* Expanded Details */}
                  {selectedAlert === alert?.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <div>
                        <h6 className="text-xs font-semibold text-foreground mb-2">Affected Credits</h6>
                        <div className="flex flex-wrap gap-2">
                          {alert?.affectedCredits?.map((credit, index) => (
                            <span key={index} className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">
                              {credit}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h6 className="text-xs font-semibold text-foreground mb-2">Recommendation</h6>
                        <p className="text-xs text-muted-foreground">{alert?.recommendation}</p>
                      </div>

                      <div>
                        <h6 className="text-xs font-semibold text-foreground mb-2">Additional Details</h6>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(alert?.details)?.map(([key, value]) => (
                            <div key={key}>
                              <span className="text-muted-foreground capitalize">
                                {key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}:
                              </span>
                              <span className="ml-1 text-foreground">
                                {Array.isArray(value) ? value?.join(', ') : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex space-x-2 pt-2">
                        <button className="flex-1 px-3 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-smooth">
                          Investigate
                        </button>
                        <button className="flex-1 px-3 py-2 bg-secondary text-secondary-foreground text-xs font-medium rounded-lg hover:bg-secondary/90 transition-smooth">
                          Mark Resolved
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Recent Resolved Alerts */}
      {resolvedAlerts?.length > 0 && (
        <div className="p-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Recently Resolved</h4>
          <div className="space-y-2">
            {resolvedAlerts?.slice(0, 2)?.map((alert) => (
              <div key={alert?.id} className="flex items-center space-x-3 p-2 bg-muted/30 rounded-lg">
                <Icon 
                  name="CheckCircle" 
                  size={16} 
                  className="text-success" 
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{alert?.title}</p>
                  <p className="text-xs text-muted-foreground">{formatTimestamp(alert?.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* No Alerts State */}
      {activeAlerts?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="ShieldCheck" size={48} className="mx-auto mb-4 text-success opacity-50" />
          <h4 className="text-lg font-medium text-foreground mb-2">All Clear</h4>
          <p className="text-sm text-muted-foreground">
            No active compliance alerts detected. Your credit portfolio maintains high integrity standards.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComplianceAlerts;